import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileSignature,
  PlusCircle,
  Search,
  Calendar,
  Eye,
  Copy,
  Loader2,
  Pencil,
  Download,
  ListChecks,
} from 'lucide-react';
import { vendorContractApi } from '../../services/vendorContractService';
import { downloadContractPdf } from '../../utils/vendorContractPdf';
import { useToastContext } from '../../contexts/ToastContext';
import {
  VendorPageHeader,
  VendorGuideCallout,
  VendorEmptyState,
} from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'sent', label: 'Sent' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'signed', label: 'Signed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'expired', label: 'Expired' },
];

const statusColor = (status) => {
  const map = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    sent: 'bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300',
    viewed: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    signed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
    expired: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
  };
  return map[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
};

const VendorContracts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const { success, error: showError } = useToastContext();

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-contracts', searchTerm, status],
    queryFn: () =>
      vendorContractApi.list({
        search: searchTerm || undefined,
        status: status || undefined,
        per_page: 30,
      }),
  });

  const contracts = data?.data || [];

  const handleDownload = useCallback(
    async (contractId) => {
      setDownloadingId(contractId);
      try {
        const res = await vendorContractApi.getOne(contractId);
        const contract = res?.data;
        if (!contract) throw new Error('Contract not found');
        await downloadContractPdf(contract);
        success('Downloaded', 'Contract PDF saved to your device.');
      } catch (e) {
        console.error(e);
        showError('Download failed', e?.response?.data?.message || 'Could not create PDF. Try again.');
      } finally {
        setDownloadingId(null);
      }
    },
    [success, showError]
  );

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Contracts"
        subtitle="Create contracts from templates, draft with AI, and collect online signatures."
        actions={
          <Link
            to="/vendor/contracts/create"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium !rounded-xl shadow-sm bg-primary-600 text-white hover:bg-primary-700"
          >
            <PlusCircle className="w-4 h-4" />
            New contract
          </Link>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.contracts.title}
        steps={VENDOR_PAGE_GUIDES.contracts.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5 space-y-3">
          <div
            className="inline-flex flex-wrap rounded-xl bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Contract status"
          >
            {STATUS_TABS.map((tab) => {
              const isActive = status === tab.key;
              return (
                <button
                  key={tab.key || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setStatus(tab.key)}
                  className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading contracts…</p>
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-300 px-4 py-3 text-sm">
              Failed to load contracts.
            </div>
          ) : contracts.length === 0 ? (
            <VendorEmptyState
              icon={FileSignature}
              title="No contracts yet"
              description="Start from a template or draft with AI, sign as the vendor, then send a link for the client to sign."
              tips={[
                'Pick a template or generate with AI',
                'Add your logo and vendor signature',
                'Email the signing link — no client account needed',
              ]}
              actionLabel="Create contract"
              actionTo="/vendor/contracts/create"
            />
          ) : (
            <div className="space-y-3">
              {contracts.map((c) => (
                <div
                  key={c.id}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4 sm:p-5 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0 flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-400">
                        <FileSignature className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="font-semibold text-gray-900 dark:text-gray-50 truncate">
                            {c.title}
                          </p>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(c.status)}`}
                          >
                            {c.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {c.contract_number} · {c.client_name}
                          {c.client_email ? ` · ${c.client_email}` : ''}
                        </p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3.5 h-3.5 shrink-0" />
                          {c.created_at
                            ? new Date(c.created_at).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                          {c.signed_at && (
                            <span className="text-emerald-700 dark:text-emerald-400">
                              · Signed {new Date(c.signed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0 sm:pl-12">
                      {!['signed', 'cancelled'].includes(String(c.status || '').toLowerCase()) && (
                        <Link
                          to={`/vendor/contracts/${c.id}/edit`}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 !rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
                          title="Edit contract"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </Link>
                      )}
                      <Link
                        to={`/vendor/contracts/create?from=${c.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 !rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
                        title="Copy for another client"
                      >
                        <Copy className="w-4 h-4" />
                        Copy
                      </Link>
                      <Link
                        to={`/vendor/contracts/${c.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 !rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <Eye className="w-4 h-4" />
                        Open
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDownload(c.id)}
                        disabled={downloadingId === c.id}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-black dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-gray-800 dark:hover:bg-white disabled:opacity-60"
                        title="Download PDF"
                      >
                        {downloadingId === c.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span className="hidden sm:inline">PDF</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorContracts;
